<script setup lang="ts">
//#region imports
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { computed, h, nextTick, ref, resolveComponent, watch, onMounted, onBeforeUnmount } from 'vue';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useApi } from '@/api';
import { useSessionAction } from '@/composables/useSessionAction';
import { isErrorResponse, type FormErrors } from '@/types/core';
import _ from '@/setup/lodash';
import { useEventBus } from '@/setup/eventBus';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type {
  GetAdminsData,
  GetAdminsResponse,
  GetTeamsResponse,
} from '@/types/openapi-ts';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
import type { ConfirmParams } from '@/components/Popups/Confirm.vue';
//#endregion

//#region types
interface UserAction {
  name: string;
  icon: string;
  iconColor: string;
  type: 'positive' | 'negative';
  click: (item: Admin) => void;
  isHide?: (item: Admin) => boolean;
  link?: (item: Admin) => string;
}

type ConfirmData = NonNullable<ConfirmParams['data']>;

type Admin = GetAdminsResponse['payload'][number];
type Team = GetTeamsResponse['payload'][number];
type GetAdminsQuery = NonNullable<GetAdminsData['query']>;

interface Props {
  teamInfo?: Partial<Team>;
}

interface Emits {
  'refresh-users-list': [];
  'refresh-members': [];
  'refresh': [];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  teamInfo: () => ({}),
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const LS_PATH = 'TeamInfoUsers';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<Admin>[] = [
  {
    name: 'account_status',
    width: '32',
    align: 'center',
    sortable: 'custom',
    linkAppend: renderStatus,
  },
  {
    name: 'name',
    class: 't:!justify-start',
    sortable: 'custom',
    minWidth: '120',
    align: 'left',
    link: getUserProfile,
    linkAppend: renderLastSeen,
  },
  {
    name: 'email',
    minWidth: '150',
    align: 'left',
    sortable: 'custom',
  },
  {
    name: 'last_active',
    width: '120',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'format-date',
  },
  {
    name: 'teams',
    sortable: false,
    width: '60',
    align: 'center',
    headerAlign: 'center',
    linkAppend: (row) => renderTeamsList(row),
  },
];
//#endregion

//#region composables
const api = useApi();
const popupManager = usePopupManager();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const $i18n = useI18n();
const eventBus = useEventBus();
const { onSessionUnlock } = useSessionAction();
const { searchQuery } = useTableSearch();
const { limit, count, page } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions: {
    sort_column: 'name',
    sort_dir: 'asc',
  },
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region state
const tableData = ref<Admin[]>([]);
const statusUser = ref(undefined);

const errors = ref<FormErrors>({});
//#endregion

//#region computed
const currentLang = computed(() => {
  return $i18n.locale.value;
});

const routeId = computed(() => {
  const routeParam = route.params.id;
  const id = Array.isArray(routeParam) ? (routeParam[0] ?? '') : (routeParam ?? '');
  return id === 'all' ? '' : id;
});

const userActionsList = computed<UserAction[]>(() => [
  {
    name: 'view_profile',
    type: 'positive',
    icon: 'eye',
    iconColor: 'var(--text-primary)',
    click: goUserProfile,
  },
  {
    name: 'edit_profile',
    type: 'positive',
    icon: 'pen',
    iconColor: 'var(--text-primary)',
    click: editUserProfile,
  },
  {
    name: 'permissions',
    type: 'positive',
    icon: 'unlock',
    iconColor: 'var(--text-primary)',
    isHide: (item) => (
      item.teams.some((team) => team.type === 'owners') || item.teams_count === 0
    ),
    click: permissionsHandler,
  },
  {
    name: 'unblock',
    type: 'positive',
    icon: 'check',
    iconColor: 'var(--text-primary)',
    isHide: (item) => item.account_status === 'active',
    click: unblockHandler,
  },
  {
    name: 'reset_password',
    type: 'negative',
    icon: 'key',
    iconColor: 'var(--text-negative)',
    click: resetUserPasswordHandler,
  },
  {
    name: 'reset_2fa',
    type: 'negative',
    icon: 'laptop-mobile',
    iconColor: 'var(--text-negative)',
    click: reset2faHandler,
  },
  {
    name: 'block',
    type: 'negative',
    icon: 'ban',
    iconColor: 'var(--text-negative)',
    isHide: (item) => item.account_status === 'blocked',
    click: blockHandler,
  },
  {
    name: 'delete_from_team',
    type: 'negative',
    icon: 'user-minus',
    iconColor: 'var(--text-negative)',
    isHide: () => !routeId.value,
    click: delFromTeamHandler,
  },
  {
    name: 'delete_from_system',
    type: 'negative',
    icon: 'trash-alt',
    iconColor: 'var(--text-negative)',
    click: delFromSystemHandler,
  },
]);
//#endregion

//#region methods
function openConfirm(data: ConfirmData, action: () => Promise<unknown>) {
  const confirmPopup = popupManager.confirm.open({
    data,
    listeners: {
      success: async () => {
        const context = await confirmPopup;

        try {
          await action();
          context.handleClose();
        } catch (error: unknown) {
          setErrors(error);
        }
      },
    },
  });
}

function delFromSystemHandler(row: Admin) {
  openConfirm({
    title: t('team_popup.delete_title_system', { name: row.name }),
    message: t('team_popup.delete_msg_system', { name: row.name }),
    bodyIcon: 'trash-alt',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'trash-alt',
    confirmTitle: t('team_popup.delete_action_system'),
    confirmType: 'negative',
  }, async () => {
    await api.deleteAdmins(row.id);
    fetchData();
    emit('refresh');
  });
}

function delFromTeamHandler(row: Admin) {
  openConfirm({
    title: t('team_popup.delete_title_team', {
      name: row.name,
      teamName: props.teamInfo.name,
    }),
    message: t('team_popup.delete_msg_team', {
      name: row.name,
      teamName: props.teamInfo.name,
    }),
    bodyIcon: 'trash-alt',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'trash-alt',
    confirmTitle: t('team_popup.delete_action_team'),
    confirmType: 'negative',
  }, async () => {
    await api.postDetachFromTeam(row.id, { team_id: routeId.value });
    fetchData();
    emit('refresh-members');
  });
}

function blockHandler(row: Admin) {
  openConfirm({
    title: t('team_popup.block_title', { name: row.name }),
    message: t('team_popup.block_msg', { name: row.name }),
    bodyIcon: 'lock',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'lock',
    confirmTitle: t('team_popup.block_action'),
    confirmType: 'negative',
  }, async () => {
    await api.postBlock(row.id);
    row.account_status = 'blocked';
  });
}

function unblockHandler(row: Admin) {
  openConfirm({
    title: t('team_popup.unblock_title', { name: row.name }),
    message: t('team_popup.unblock_msg', { name: row.name }),
    bodyIcon: 'unlock',
    bodyIconColor: 'var(--text-positive)',
    confirmIcon: 'unlock',
    confirmTitle: t('team_popup.unblock_action'),
    confirmType: 'positive',
  }, async () => {
    await api.postUnblock(row.id);
    row.account_status = 'active';
  });
}

function reset2faHandler(row: Admin) {
  openConfirm({
    title: t('team_popup.reset_2fa_title', { name: row.name }),
    message: t('team_popup.reset_2fa_msg', { name: row.name }),
    bodyIcon: 'exclamation-triangle',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'check',
    confirmTitle: t('team_popup.reset_2fa_action'),
    confirmType: 'negative',
  }, () => api.postResetTotp(row.id));
}

function resetUserPasswordHandler(row: Admin) {
  openConfirm({
    title: t('team_popup.reset_pass_title', { name: row.name }),
    message: t('team_popup.reset_pass_msg', { name: row.name }),
    bodyIcon: 'exclamation-triangle',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'check',
    confirmTitle: t('team_popup.reset_pass_action'),
    confirmType: 'negative',
  }, () => api.postResetPassword(row.id));
}

function goUserProfile(row: Admin) {
  router.push(getUserProfile(row));
}

function permissionsHandler(row: Admin) {
  popupManager.userPermissions.open({
    data: {
      id: routeId.value,
      userInfo: row,
    },
  });
}

function editUserProfile(row: Admin) {
  popupManager.editTeamsUser.open({
    data: {
      user: row,
    },
    listeners: {
      refresh: fetchData,
    },
  });
}

function renderTeamsList(item: Admin) {
  if(item.teams_count === 0) {
    return item.teams_count;
  }

  const UiDropdown = resolveComponent('ui-dropdown');
  const teams = item.teams.map((team) => team.name);

  return h(
    UiDropdown,
    {
      itemsList: teams,
      hoverActive: false,
    },
    {
      default: () => h(
        'div',
        { class: 'team-info-users__teams-list' },
        {
          default: () => item.teams_count,
        }),
    });
}

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

function _query() {
  const params = {
    search: searchQuery.value,
    team_id: routeId.value,
    ...optionsSort.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  } as GetAdminsQuery;

  if(statusUser.value) {
    params.account_status = statusUser.value;
  }

  return params;
}

function fetchData() {
  emit('refresh-members');
  let options = _query();
  api.getAdmins(options).then((response) => {
    tableData.value = response.data.payload;
    count.value = response.data.misc?.count ?? 0;
  });
}

function renderStatus(item: Admin) {
  return h(resolveComponent('ui-icon'), {
    color: item.account_status === 'active'
      ? 'var(--text-primary)'
      : 'var(--text-negative)',
    name: item.account_status === 'active' ? 'check' : 'ban',
    size: 12,
  });
}

function getUserProfile(row: Admin) {
  const teamId = routeId.value;
  if (teamId) {
    return `/teams/user/${row.id}?team_id=${teamId}`;
  }
  return `/teams/user/${row.id}`;
}

function renderLastSeen(item: Admin) {
  const days = item.last_seen_days_ago;
  if (days === null || days < 14) return null;

  const UiDropdown = resolveComponent('ui-dropdown');

  return h(
    UiDropdown,
    {},
    {
      default: () => h(
        'div',
        { class: 't:ml-4' },
        h(resolveComponent('ui-action'), {
          color: 'var(--text-negative)',
          icon: 'exclamation-circle',
        }),
      ),
      dropdown: () => h('div', {
        class: 'team-info-users__last-seen',
        innerHTML: t('team_users.list.alert.last_seen', { days }),
      }),
    },
  );
}

function addUsersHandler() {
  popupManager.attachUserToTeam.open({
    data: {
      context: {
        mode: 'select-user',
        team: props.teamInfo,
      },
    },
    listeners: {
      refresh: () => {
        emit('refresh-members');
        fetchData();
      },
    },
  });
}

function createUserHandler() {
  popupManager.createTeamUser.open({
    listeners: {
      success: () => {
        emit('refresh-users-list');
        fetchData();
      },
    },
  });
}

function setErrors(error: unknown) {
  if (!isErrorResponse(error)) {
    throw error;
  }

  errors.value = _.cloneDeep(error.data.errors) ?? {};
}
//#endregion

//#region watch
watch(() => route.params.id, () => {
  searchQuery.value = '';
  handleRefresh();
});
//#endregion

//#region hooks
onSessionUnlock.value = fetchData;
fetchData();

onMounted(() => {
  eventBus.$on('addUserNewTeam', fetchData);
});

onBeforeUnmount(() => {
  eventBus.$off('addUserNewTeam', fetchData);
});
//#endregion
</script>

<template>
  <div class="team-info-users">
    <UiToolbar>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          is-search
          autosize
          @search="handleRefresh"
        />
        <UiSelectElement
          :key="currentLang"
          v-model="statusUser"
          :placeholder="$t('ui.filters.select.all')"
          prepend-icon="filter"
          :list="[
            {
              name: t('affiliates.listView.toolbar[1].statusFilter.active'),
              id: 'active',
            },
            {
              name: t('affiliates.listView.toolbar[1].statusFilter.blocked'),
              id: 'blocked',
            }
          ]"
          width="initial"
          is-dynamic
          clearable
          @change="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
        <UiButton
          v-if="props.teamInfo.id !== 'all'"
          icon="user-plus"
          @click="addUsersHandler"
        >
          {{ t('ui.buttons.add') }}
        </UiButton>
        <UiButton
          icon="plus"
          @click="createUserHandler"
        >
          {{ t('ui.buttons.create') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      i18n-path="team_users.list"
      element-loading-custom-class="data-loader-spinner"
      is-static
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="80"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <UiDropdown
              popper-class="team-info-users__action-popper"
            >
              <span>
                <ActionIcon
                  icon="ellipsis-h"
                  :tooltip="t('team_users.list.actions.name')"
                />
              </span>
              <template v-slot:dropdown>
                <div
                  v-for="(item, key) in userActionsList.filter((action: UserAction) => !action.isHide?.(scope.row))"
                  :key="key"
                  :class="[
                    'team-info-users__action-item',
                    `team-info-users__action-item_${item.type}`
                  ]"
                  @click="item.click(scope.row)"
                >
                  <UiIcon
                    :name="item.icon"
                    :color="item.iconColor"
                    class="team-info-users__action-icon"
                  />
                  {{ t(`team_users.list.actions.${item.name}`) }}
                </div>
              </template>
            </UiDropdown>
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang="scss">
.team-info-users {
  &__last-seen {
    font-size: 14px;
    line-height: 18px;
    padding: 8px 16px;
    span {
      color: var(--text-negative);
      font-weight: 500;
    }
  }
  &__teams-list {
    color: var(--text-link);
    font-size: 12px;
  }
  &__action-popper {
    .el-dropdown-menu {
      padding: 8px;
    }
  }
  &__action-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    cursor: pointer;
    border-radius: 8px;
    font-size: 12px;
    line-height: 18px;

    &_positive{
      &:hover {
        background: var(--background-brand-subtle);
      }
    }
    &_negative {
      &:hover {
        background: var(--background-negative-subtle);
      }
    }
  }
  &__action-icon{
    width: 18px;
  }
}
</style>
